import React from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { BookOpen, ClipboardCheck, FileBadge, GraduationCap, LayoutDashboard, LogOut, Settings, ShieldCheck, UserCog, UsersRound, Layers3 } from 'lucide-react';
import { useLms } from '../context/LmsContext';
import { defaultDashboard, normalizeUserRoles } from '../utils/navigation';
import { fullName } from '../utils/lmsSelectors';
import { RoleBadge } from './ui';

const navByRole = {
  admin: [
    { to: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { to: '/admin/users', label: 'Users', icon: UserCog },
    { to: '/admin/groups', label: 'Groups', icon: UsersRound },
    { to: '/admin/courses', label: 'Courses', icon: BookOpen },
    { to: '/admin/programs', label: 'Programs', icon: Layers3 },
    { to: '/admin/assignments', label: 'Assignments', icon: ClipboardCheck },
    { to: '/admin/reports', label: 'Reports', icon: LayoutDashboard },
    { to: '/admin/certificates', label: 'Certificates', icon: FileBadge },
    { to: '/admin/settings', label: 'Settings', icon: Settings }
  ],
  trainer: [
    { to: '/trainer/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { to: '/trainer/courses', label: 'My Courses', icon: BookOpen },
    { to: '/trainer/courses/new', label: 'New Course', icon: GraduationCap },
    { to: '/trainer/question-bank', label: 'Question Bank', icon: ShieldCheck },
    { to: '/trainer/reviews', label: 'Reviews', icon: ClipboardCheck },
    { to: '/trainer/grades', label: 'Grades', icon: FileBadge }
  ],
  learner: [
    { to: '/learner/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { to: '/learner/my-courses', label: 'My Courses', icon: BookOpen },
    { to: '/learner/grades', label: 'Grades', icon: ClipboardCheck },
    { to: '/learner/certificates', label: 'Certificates', icon: FileBadge }
  ]
};

export default function Layout({ role }) {
  const { currentUser, logout, storageStatus, updateUser } = useLms();
  const navigate = useNavigate();
  const links = navByRole[role] || [];
  const assignedRoles = normalizeUserRoles(currentUser);

  const handleRoleChange = (nextRole) => {
    if (!currentUser || nextRole === currentUser.role) return;
    updateUser(currentUser.id, { role: nextRole }, currentUser.id);
    navigate(defaultDashboard(nextRole));
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <NavLink to={defaultDashboard(role)} className="brand">
          <span className="brand-mark">AE</span>
          <span>
            <strong>Abundio Academy</strong>
            <small>Energy Training</small>
          </span>
        </NavLink>
        <nav className="side-nav">
          {links.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink key={item.to} to={item.to} className={({ isActive }) => isActive ? 'active' : ''}>
                <Icon size={18} />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </nav>
      </aside>
      <main className="main-panel">
        <header className="topbar">
          <div>
            <span className="muted small">Signed in as</span>
            <strong>{fullName(currentUser)}</strong>
          </div>
          <div className="topbar-actions">
            <span className={storageStatus?.error ? 'storage-pill storage-error' : 'storage-pill'}>Supabase Auth</span>
            <label className="role-switcher">
              <span>Current role</span>
              <select value={currentUser?.role || role} onChange={(event) => handleRoleChange(event.target.value)}>
                {assignedRoles.map((assignedRole) => (
                  <option key={assignedRole} value={assignedRole}>{assignedRole.charAt(0).toUpperCase() + assignedRole.slice(1)}</option>
                ))}
              </select>
            </label>
            <RoleBadge role={currentUser?.role} />
            <NavLink to="/profile" className="profile-link">Profile</NavLink>
            <button className="icon-button" onClick={handleLogout}><LogOut size={18} /> Logout</button>
          </div>
        </header>
        <div className="content"><Outlet /></div>
      </main>
    </div>
  );
}
